// Lab quality control and microbiology lists (docs/plans/phase3-lab-qc-and-microbiology.md
// §3). QC values are not patient data; culture values travel only in request bodies.
import { queryOptions } from '@tanstack/react-query';
import {
  labMicroItemSchema,
  labMicroListSchema,
  labReportSchema,
  qcSeriesListSchema,
  qcSeriesSchema,
  qcSetupSchema,
  type LabMicroList,
  type SaveCultureBody,
} from '@wodi/shared';
import type { WestgardRule } from '@wodi/domain';

import { api } from './client.ts';

export const qcKeys = {
  all: ['lab', 'qc'] as const,
  setup: ['lab', 'qc', 'setup'] as const,
  series: ['lab', 'qc', 'series'] as const,
};

export const qcSetupQuery = queryOptions({
  queryKey: qcKeys.setup,
  queryFn: () => api('GET', '/lab/qc/setup', { schema: qcSetupSchema }),
});

export const qcSeriesListQuery = queryOptions({
  queryKey: [...qcKeys.series, 'list'],
  queryFn: () => api('GET', '/lab/qc/series', { schema: qcSeriesListSchema }),
});

export const qcSeriesQuery = (targetId: string) =>
  queryOptions({
    queryKey: [...qcKeys.series, targetId],
    queryFn: () => api('GET', `/lab/qc/targets/${targetId}`, { schema: qcSeriesSchema }),
  });

export function createQcMaterial(body: {
  name: string;
  levelLabel: string;
  manufacturer: string | null;
}) {
  return api('POST', '/admin/lab/qc/materials', { body, schema: qcSetupSchema });
}

export function updateQcMaterial(
  materialId: string,
  body: {
    name: string;
    levelLabel: string;
    manufacturer: string | null;
    status: 'active' | 'inactive';
  },
) {
  return api('PATCH', `/admin/lab/qc/materials/${materialId}`, { body, schema: qcSetupSchema });
}

export function createQcLot(
  materialId: string,
  body: { lotNumber: string; expiresOn: string; analyzerControlId: string | null },
) {
  return api('POST', `/admin/lab/qc/materials/${materialId}/lots`, { body, schema: qcSetupSchema });
}

export function updateQcLot(
  lotId: string,
  body: {
    expiresOn: string;
    analyzerControlId: string | null;
    status: 'active' | 'inactive';
    statusReason: string | null;
  },
) {
  return api('PATCH', `/admin/lab/qc/lots/${lotId}`, { body, schema: qcSetupSchema });
}

export function setQcTarget(
  lotId: string,
  body: { analyteId: string; analyzerId: string | null; mean: string; sd: string; source: string },
) {
  return api('POST', `/admin/lab/qc/lots/${lotId}/targets`, { body, schema: qcSetupSchema });
}

export function retireQcTarget(targetId: string) {
  return api('POST', `/admin/lab/qc/targets/${targetId}/retire`, { schema: qcSetupSchema });
}

export function setQcRules(analyteId: string, rules: WestgardRule[]) {
  return api('PUT', `/admin/lab/qc/rules/${analyteId}`, { body: { rules }, schema: qcSetupSchema });
}

export function recordQcRun(body: {
  targetId: string;
  value: string;
  runAt?: string;
  comment: string | null;
}) {
  return api('POST', '/lab/qc/runs', { body, schema: qcSeriesSchema });
}

export function qcRunInError(runId: string, reason: string) {
  return api('POST', `/lab/qc/runs/${runId}/entered-in-error`, {
    body: { reason },
    schema: qcSeriesSchema,
  });
}

export function addQcCorrectiveAction(runId: string, note: string) {
  return api('POST', `/lab/qc/runs/${runId}/corrective-actions`, {
    body: { note },
    schema: qcSeriesSchema,
  });
}

export function requestQcOverride(runId: string, reason: string) {
  return api('POST', `/lab/qc/runs/${runId}/overrides`, {
    body: { reason },
    schema: qcSeriesSchema,
  });
}

export function decideQcOverride(
  overrideId: string,
  decision: 'approve' | 'decline',
  note: string | null,
) {
  return api('POST', `/lab/qc/overrides/${overrideId}/${decision}`, {
    body: { note },
    schema: qcSeriesSchema,
  });
}

// --- Microbiology ---------------------------------------------------------------------------

export const microListQuery = (list: LabMicroList) =>
  queryOptions({
    queryKey: ['lab', 'catalogue', 'micro', list],
    queryFn: () => api('GET', `/lab/micro/${list}`, { schema: labMicroListSchema }),
  });

export function addMicroItem(list: LabMicroList, name: string) {
  return api('POST', `/admin/lab/micro/${list}`, { body: { name }, schema: labMicroItemSchema });
}

export function updateMicroItem(
  list: LabMicroList,
  itemId: string,
  body: { name?: string; status?: 'active' | 'inactive' },
) {
  return api('PATCH', `/admin/lab/micro/${list}/${itemId}`, { body, schema: labMicroItemSchema });
}

export function saveCulture(reportId: string, body: SaveCultureBody) {
  return api('PUT', `/lab-reports/${reportId}/culture`, { body, schema: labReportSchema });
}

export function issuePreliminaryReport(reportId: string, expectedRevision: number) {
  return api('POST', `/lab-reports/${reportId}/preliminary`, {
    body: { expectedRevision },
    schema: labReportSchema,
  });
}
